<template>
    <section class="dmarc-gen">
        <div class="skinny-form">
            <h2>{{ $t('email.auth.authModal.dmarcSettings.header.title') }}</h2>

            <!-- eslint-disable vue/no-v-html -->
            <p v-html="$t('email.auth.authModal.dmarcSettings.header.description', { dmarcRecordLink: dmarcRecordLink })" />

            <p v-html="$t('email.auth.authModal.dmarcSettings.description.verifyStatus', { verifyStatusLink: verifyStatusLink })" />
            <!-- eslint-enable vue/no-v-html -->

            <div class="dmarc-gen-selector">
                <DsCheckbox
                    v-model="dmarc.createOrUpdateDmarc"
                    class="dmarc-checkbox"
                    :label="$t('email.auth.authModal.dmarcSettings.checkbox')"
                    data-qa="create-or-update-dmarc-checkbox"
                    @input="updateDmarc"
                />
            </div>
        </div>
        <div v-if="dmarc.createOrUpdateDmarc" class="dmarc-gen-container">
            <p>{{ $t('email.auth.authModal.dmarcSettings.description.failedDMARC') }}</p>

            <DsButtonToggle
                    v-model="dmarc.policy"
                    class="policy-toggle"
                    :options="POLICY_OPTIONS"
                    prevent-single-deselect
                    @input="updateDmarc"
            />

            <section class="policy-sub-options">
                <div class="card-container">
                    <DsCard class="card-width">
                        <DsCardHeader class="policy-description-header">
                            <h3><strong>{{ $t(`email.auth.authModal.dmarcSettings.policyDescription.${dmarc.policy}.header`) }}</strong></h3>

                            <DsStatus v-if="dmarc.policy === POLICY_QUARANTINE" emphasis="high">
                                {{ $t('global.recommended') }}
                            </DsStatus>
                        </DsCardHeader>

                        <DsCardBody>
                            <p>{{ $t(`email.auth.authModal.dmarcSettings.policyDescription.${dmarc.policy}.description`) }}</p>
                        </DsCardBody>

                        <DsCardBody v-if="showPolicySubOptions">
                            <div class="pct-section">
                                <DsInputField
                                        v-model="dmarc.pct"
                                        name="pct"
                                        :label="$t('email.auth.authModal.dmarcSettings.pctLabel', { policy: $t(`email.auth.authModal.dmarcSettings.policy.${dmarc.policy}`) })"
                                        type="number"
                                        :min="0"
                                        :max="100"
                                        data-qa="dmarc-pct"
                                        @input="updateDmarc"
                                >
                                    <template #leading>
                                        <DsIcon name="percent" />
                                    </template>
                                </DsInputField>

                                <DsPopover
                                        position="right"
                                        position-fixed
                                >
                                    <template #reference>
                                        <DsIconButton class="pct-tooltip-button" name="info-circle" />
                                    </template>

                                    <template #default>
                                        <div class="pct-tooltip">
                                            {{ $t('email.auth.authModal.dmarcSettings.pctInfo') }}
                                        </div>
                                    </template>
                                </DsPopover>
                            </div>

                            <p>{{ $t(`email.auth.authModal.dmarcSettings.aggregateReportsDescription`) }}</p>

                            <DsInputField
                                v-if="isAddValidationToEmailDomainVerificationEnabled"
                                v-model="dmarc.rua"
                                required
                                name="rua"
                                :label="$t('global.email')"
                                :invalid="!isRuaValid"
                                :submitted="!isRuaValid"
                                trim
                                data-qa="dmarc-rua"
                                @input="updateDmarc"
                            >
                                <template #error>
                                    {{ $t('email.auth.authModal.dmarcSettings.validated.error.rua') }}
                                </template>
                            </DsInputField>

                            <DsInputField
                                    v-else
                                    v-model="dmarc.rua"
                                    name="rua"
                                    :label="$t('global.email')"
                                    :invalid="!isRuaValid"
                                    :submitted="submitted"
                                    trim
                                    data-qa="dmarc-rua"
                                    @input="updateDmarc"
                            >
                                <template #error>
                                    {{ $t('email.auth.authModal.dmarcSettings.validated.error.rua') }}
                                </template>
                            </DsInputField>
                        </DsCardBody>
                    </DsCard>

                    <DsCard class="card-width med-show">
                        <DsCardHeader>
                            <h3><strong>{{ $t(`email.auth.authModal.dmarcSettings.whatWillHappen`) }}</strong></h3>
                        </DsCardHeader>

                        <DsCardBody>
                            <p>{{ $t(`email.auth.authModal.dmarcSettings.policyDescription.${dmarc.policy}.whatWillHappen`) }}</p>
                        </DsCardBody>

                        <div v-if="showPolicySubOptions">
                            <DsCardHeader>
                                <h3><strong>{{ $t(`email.auth.authModal.dmarcSettings.aggregateReport`) }}</strong></h3>
                            </DsCardHeader>

                            <DsCardBody>
                                <p>{{ $t(`email.auth.authModal.dmarcSettings.aggregateReportBody`) }}</p>
                            </DsCardBody>
                        </div>
                    </DsCard>
                </div>
            </section>
        </div>
    </section>
</template>

<script>
import { patterns } from '@/utils/email.util';

const POLICY_NONE = 'none';
const POLICY_QUARANTINE = 'quarantine';
const POLICY_REJECT = 'reject';
const DMARC_PCT = 5;

export default {
    emits: ['update-dmarc'],

    props: {
        submitted: {
            type: Boolean,
            default: false,
        },
        isAddValidationToEmailDomainVerificationEnabled: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            dmarc: {
                createOrUpdateDmarc: false,
                policy: POLICY_QUARANTINE,
                pct: DMARC_PCT,
                rua: '',
            },
            dmarcRecordUrl: 'https://help.keap.com/help/dmarc',
            verifyStatusUrl: 'https://dmarcian.com/dmarc-inspector/#dm-inspect-dmarc',
            isRuaValid: false,
        };
    },

    created() {
        this.POLICY_QUARANTINE = POLICY_QUARANTINE;

        const POLICY_OPTIONS = [
            {
                label: this.$t('email.auth.authModal.dmarcSettings.policy.quarantine'),
                value: POLICY_QUARANTINE,
            },
            {
                label: this.$t('email.auth.authModal.dmarcSettings.policy.reject'),
                value: POLICY_REJECT,
            },
            {
                label: this.$t('email.auth.authModal.dmarcSettings.policy.none'),
                value: POLICY_NONE,
            },
        ];

        this.POLICY_OPTIONS = POLICY_OPTIONS;
    },

    computed: {
        dmarcRecordLink() {
            return `<a href="${this.dmarcRecordUrl}" target="_blank" rel="noopener noreferrer">${this.$t('email.auth.authModal.dmarcSettings.header.dmarcRecord')}</a>`;
        },

        verifyStatusLink() {
            return `<a href="${this.verifyStatusUrl}" target="_blank" rel="noopener noreferrer">${this.$t('email.auth.authModal.dmarcSettings.description.verifyTheStatus')}</a>`;
        },

        showPolicySubOptions() {
            return this.dmarc.policy === POLICY_QUARANTINE || this.dmarc.policy === POLICY_REJECT;
        },
    },

    methods: {
        updateDmarc() {
            this.isRuaValid = this.isValidEmailAddress(this.dmarc.rua);
            this.$emit('update-dmarc', this.dmarc);
        },

        isValidEmailAddress(rua) {
            if (this.isAddValidationToEmailDomainVerificationEnabled) {
                if (!rua) {
                    return true;
                }

                return RegExp(patterns.email).test(rua);
            }

            return rua.length === 0 || (rua.length > 0 && rua.indexOf('@') > -1);
        },
    }
};

</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
    .dmarc-gen h2 {
        padding-bottom: $spacing-200;
    }

    .dmarc-gen-selector {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        a {
            margin: $spacing-200;
        }
    }

    .dmarc-gen-container {
        margin-top: $spacing-200;
    }

    .card-container {
        display: flex;
    }

    .card-width {
        width: 100%;
        margin-right: $spacing-200;
        background: $color-ink-050;
    }

    .ds-card-header, .ds-card-body {
        padding: $spacing-200 $spacing-200 0;
    }

    .dmarc-checkbox {
        padding: 0;
    }

    .pct-section {
        display: flex;
    }

    .pct-tooltip-button {
        margin: 0 0 0 $spacing-100;
        --icon-color: #{$color-gray-darker};
    }

    .pct-tooltip {
        padding: $spacing-200;
        font-size: $font-size-md;
        line-height: $line-height-lg;
        .text-bold {
            font-weight: $font-weight-bold;
        }
    }

    .policy-sub-options {
        display: flex;
        width: 100%;
        margin-left: 0;
        margin-right: 0;
    }

    .skinny-form {
        max-width: 31.25rem;
    }


    .policy-toggle ::v-deep {
        margin-bottom: $spacing-200;
        .button-toggle-option {
            color: $color-blue;
        }
        .button-toggle-option.selected {
            color: $color-white;
        }
    }

    .policy-description-header {
        display: flex;
        padding: $spacing-200 $spacing-200 0;

        h3 {
            margin-right: $spacing-100;
        }
    }

    @media($extra-small) {
        .med-show {
            display: none;
        }
    }

    @media($medium) {
        .med-show {
            display: block;
        }
    }
</style>
